import React from 'react';
import { AlertTriangle } from 'lucide-react';
export function Cox2Section() {
  const consequences = ['Te inflamás después de comidas que antes tolerabas perfectamente.', 'Retenés líquido aunque tomes mucha agua.', 'Tu vientre nunca termina de aplanarse aunque bajes de peso.', 'Sentís pesadez y fatiga crónica sin razón aparente.', 'Los antiinflamatorios te alivian momentáneamente pero el problema vuelve.'];
  return <section className="py-14 bg-[#fdfdf5]" data-id="element-126">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
        <div className="max-w-3xl mx-auto" data-id="element-128">
          {/* Headline */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-black mb-4 leading-tight text-center" data-id="element-129">
            Y mientras el camión no pasa, hay algo más que trabaja en tu contra.
          </h2>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl font-serif italic text-sage/80 text-center mb-10" data-id="element-130">
            La enzima que convirtió tu cuerpo en una zona de emergencia
            permanente.
          </p>

          {/* Opening paragraphs */}
          <div className="space-y-6 text-lg text-sage/90 mb-10" data-id="element-131">
            <p data-id="element-132">
              Además del CMM apagado, después de los 40 tu cuerpo activa otro
              mecanismo que muy pocas personas conocen y que es responsable de
              que la inflamación <strong data-id="element-133">nunca termine de irse</strong>.
            </p>
            <p data-id="element-134">
              Se llama <strong data-id="element-135">enzima COX-2</strong>. Y su trabajo original es
              protegerte.
            </p>
            <p data-id="element-136">
              Pensá en ella como la <strong data-id="element-137">alarma de incendios</strong> de tu
              cuerpo. Cuando detecta una amenaza real, suena fuerte y te
              defiende. Sin ella, no podrías combatir infecciones ni sanar
              heridas.
            </p>
            <p data-id="element-138">
              Pero después de los 40, con la{' '}
              <strong data-id="element-139">caída del estrógeno</strong> y el{' '}
              <strong data-id="element-140">aumento del cortisol</strong>, esa alarma se vuelve
              hipersensible.
            </p>
            <p className="font-semibold text-black" data-id="element-141">
              Empieza a dispararse con cualquier cosa.
            </p>
          </div>

          {/* Trigger phrases - no bullets, generous line-height */}
          <div className="space-y-4 text-center mb-8" data-id="element-142">
            <p className="text-xl sm:text-2xl font-serif text-sage/90 leading-loose" data-id="element-143">
              Con el pan integral del desayuno.
            </p>
            <p className="text-xl sm:text-2xl font-serif text-sage/90 leading-loose" data-id="element-144">
              Con el estrés de una reunión de trabajo.
            </p>
            <p className="text-xl sm:text-2xl font-serif text-sage/90 leading-loose" data-id="element-145">
              Con una copa de vino en un cumpleaños.
            </p>
            <p className="text-xl sm:text-2xl font-serif text-sage/90 leading-loose" data-id="element-146">
              Con el aceite vegetal que usás para cocinar todos los días.
            </p>
          </div>

          {/* Hammer phrase */}
          <p className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-black text-center mb-10 leading-snug" data-id="element-147">
            Suena. Y suena. Y suena.
          </p>

          {/* Consequence paragraphs */}
          <div className="space-y-6 text-lg text-sage/90 mb-10" data-id="element-148">
            <p data-id="element-149">
              Tu sistema inmune recibe la señal de emergencia y responde de la
              única forma que sabe: <strong data-id="element-150">inflamando los tejidos</strong> para
              aislar la amenaza.
            </p>
            <p data-id="element-151">
              Tu vientre se hincha. Tus tejidos retienen líquido. Tu cuerpo
              gasta toda su energía peleando contra fantasmas mientras vos te
              preguntás <strong data-id="element-152">qué hiciste mal</strong>.
            </p>
          </div>

          {/* Consequences list header */}
          <h3 className="text-xl font-serif font-bold text-black mb-6" data-id="element-153">
            Cuando la COX-2 está desregulada:
          </h3>

          {/* Consequences list with coral/terracotta alert icons */}
          <ul className="space-y-4 mb-12" data-id="element-154">
            {consequences.map((item, idx) => <li key={idx} className="flex items-start gap-4" data-id="element-155">
                <div className="mt-1 bg-[#E07A5F]/15 p-1.5 rounded-full flex-shrink-0" data-id="element-156">
                  <AlertTriangle className="w-4 h-4 text-[#C45A3C]" data-id="element-157" />
                </div>
                <span className="text-sage/90 font-medium text-lg leading-relaxed" data-id="element-158">
                  {item}
                </span>
              </li>)}
          </ul>

          {/* Emerald green highlighted box */}
          <div className="bg-[#2d6a4f] rounded-2xl p-6 sm:p-8 shadow-lg mb-10" data-id="element-159">
            <p className="text-white text-lg sm:text-xl font-serif font-semibold leading-relaxed text-center" data-id="element-160">
              El Protocolo Reset no suprime la COX-2 como lo hace el ibuprofeno.
              Le enseña a tu cuerpo a distinguir el humo real del vapor de la
              ducha. Cuando esa alarma se regula, la inflamación se apaga sola.
              El vientre se aplana. Y el cuerpo deja de estar en estado de
              emergencia permanente.
            </p>
          </div>

          {/* Transition phrase */}
          <p className="text-center text-lg sm:text-xl font-serif italic text-sage/70 leading-relaxed" data-id="element-161">
            Ahora que entendés las dos raíces del problema, te mostramos la
            ciencia exacta que usamos para apagarlas.
          </p>
        </div>
      </div>
    </section>;
}